import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Save, Loader2, Activity, CheckCircle2, AlertTriangle } from "lucide-react";
import FormError from "./ui/FormError";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { confirmDialog } from "../utils/dialogs";
import {
  SEVERITY_LABEL,
  SEVERITY_COLOR,
  STATUS_LABEL,
  STATUS_COLOR,
  typeLabel,
  bodyPartLabel,
} from "../lib/injuryLabels";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY = {
  type: "cut",
  type_other: "",
  body_part: "general",
  body_part_other: "",
  severity: "trivial",
  onset_date: "",
  description: "",
  treatment: "",
  rest_required: false,
  days_off_training: "",
  status: "active",
  resolved_date: "",
  // YCH incident-form extensions
  incident_type: "injury",
  time_of_incident: "",
  reported_by: "",
  report_received_by: "",
  location: "",
  symptoms: "",
  vitals_bp: "",
  vitals_temp: "",
  vitals_pr: "",
  vitals_spo2: "",
  vitals_sugar: "",
  actions: [],
  informed_role: "",
  informed_name: "",
  informed_time: "",
  ref_hospital: "",
  ref_time_left: "",
  ref_time_arrived: "",
  ref_doctor: "",
  ref_escort: "",
  ref_contact: "",
  ref_parent_arrival: "",
  ref_handover: "",
};

/**
 * Add / edit a single injury record. When `injury` is passed the modal opens
 * in edit mode; otherwise it's a fresh log entry for `sailor`.
 *
 * Props:
 *   open        : bool
 *   onClose     : fn()
 *   onSaved     : fn()   — fired on successful save so the parent list
 *                          can refetch
 *   sailor      : { id, name }
 *   injury      : optional existing record being edited
 *   constants   : { types, body_parts, severities, statuses }
 */
export default function InjuryFormModal({ open, onClose, onSaved, sailor, injury, constants }) {
  const [form, setForm] = useState(EMPTY);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEscClose(open, onClose);

  useEffect(() => {
    if (!open) return;
    if (injury) {
      const v = injury.vitals || {};
      const inf = injury.informed || {};
      const r = injury.referral || {};
      setForm({
        type: injury.type,
        type_other: injury.type_other || "",
        body_part: injury.body_part,
        body_part_other: injury.body_part_other || "",
        severity: injury.severity,
        onset_date: injury.onset_date || "",
        description: injury.description || "",
        treatment: injury.treatment || "",
        rest_required: !!injury.rest_required,
        days_off_training: injury.days_off_training == null ? "" : String(injury.days_off_training),
        status: injury.status || "active",
        resolved_date: injury.resolved_date || "",
        // YCH incident-form extensions
        incident_type: injury.incident_type || "injury",
        time_of_incident: injury.time_of_incident || "",
        reported_by: injury.reported_by || "",
        report_received_by: injury.report_received_by || "",
        location: injury.location || "",
        symptoms: injury.symptoms || "",
        vitals_bp: v.bp || "",
        vitals_temp: v.temp || "",
        vitals_pr: v.pr || "",
        vitals_spo2: v.spo2 || "",
        vitals_sugar: v.sugar || "",
        actions: Array.isArray(injury.actions) ? injury.actions : [],
        informed_role: inf.role || "",
        informed_name: inf.name || "",
        informed_time: inf.at_time || "",
        ref_hospital: r.hospital || "",
        ref_time_left: r.time_left || "",
        ref_time_arrived: r.time_arrived || "",
        ref_doctor: r.doctor || "",
        ref_escort: r.escort_name || "",
        ref_contact: r.contact_no || "",
        ref_parent_arrival: r.parent_arrival || "",
        ref_handover: r.handover_time || "",
      });
    } else {
      setForm({ ...EMPTY, onset_date: todayISO() });
    }
    setNoteDraft("");
    setError("");
  }, [open, injury]);

  const isEdit = !!injury;

  const save = async () => {
    setError("");
    if (!form.type || !form.body_part || !form.severity) {
      setError("Type, body part and severity are required");
      return;
    }
    if (!form.onset_date) {
      setError("Onset date is required");
      return;
    }
    if (form.type === "other" && !form.type_other.trim()) {
      setError("Please describe the injury type (since you picked 'Other')");
      return;
    }
    if (form.body_part === "general" && !form.body_part_other.trim()) {
      setError("Please specify the body part (since you picked 'General / Other')");
      return;
    }
    // Collapse all the per-field vitals / informed / referral inputs back
    // into the nested submodels the backend expects. Sending an object with
    // every value empty is fine — the backend stores it as an empty record
    // which Mongo handles cleanly.
    const trimOrNull = (s) => (s || "").trim() || null;
    const anyVital =
      form.vitals_bp || form.vitals_temp || form.vitals_pr || form.vitals_spo2 || form.vitals_sugar;
    const vitals = anyVital
      ? {
          bp: trimOrNull(form.vitals_bp),
          temp: trimOrNull(form.vitals_temp),
          pr: trimOrNull(form.vitals_pr),
          spo2: trimOrNull(form.vitals_spo2),
          sugar: trimOrNull(form.vitals_sugar),
        }
      : null;
    const anyInformed = form.informed_role || form.informed_name || form.informed_time;
    const informed = anyInformed
      ? {
          role: trimOrNull(form.informed_role),
          name: trimOrNull(form.informed_name),
          at_time: trimOrNull(form.informed_time),
        }
      : null;
    const refHas =
      form.ref_hospital || form.ref_time_left || form.ref_time_arrived || form.ref_doctor ||
      form.ref_escort || form.ref_contact || form.ref_parent_arrival || form.ref_handover;
    const referral = refHas
      ? {
          hospital: trimOrNull(form.ref_hospital),
          time_left: trimOrNull(form.ref_time_left),
          time_arrived: trimOrNull(form.ref_time_arrived),
          doctor: trimOrNull(form.ref_doctor),
          escort_name: trimOrNull(form.ref_escort),
          contact_no: trimOrNull(form.ref_contact),
          parent_arrival: trimOrNull(form.ref_parent_arrival),
          handover_time: trimOrNull(form.ref_handover),
        }
      : null;
    const payload = {
      type: form.type,
      type_other: form.type === "other" ? form.type_other.trim() : null,
      body_part: form.body_part,
      body_part_other: form.body_part === "general" ? form.body_part_other.trim() : null,
      severity: form.severity,
      onset_date: form.onset_date,
      description: form.description || null,
      treatment: form.treatment || null,
      rest_required: !!form.rest_required,
      days_off_training:
        form.days_off_training === "" || form.days_off_training == null
          ? null
          : Number(form.days_off_training),
      // YCH incident-form extensions
      incident_type: form.incident_type || null,
      time_of_incident: trimOrNull(form.time_of_incident),
      reported_by: trimOrNull(form.reported_by),
      report_received_by: trimOrNull(form.report_received_by),
      location: trimOrNull(form.location),
      symptoms: trimOrNull(form.symptoms),
      vitals,
      actions: Array.isArray(form.actions) ? form.actions : [],
      informed,
      referral,
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/injuries/${injury.id}`, {
          ...payload,
          status: form.status,
          resolved_date: form.status === "resolved" ? form.resolved_date || todayISO() : null,
        });
        if (noteDraft.trim()) {
          await api.post(`/injuries/${injury.id}/notes`, { note: noteDraft.trim() });
        }
      } else {
        await api.post("/injuries", { ...payload, sailor_id: sailor.id });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resolveNow = async () => {
    if (!injury) return;
    setSaving(true);
    setError("");
    try {
      await api.put(`/injuries/${injury.id}`, {
        status: "resolved",
        resolved_date: todayISO(),
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not mark resolved");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!injury) return;
    if (!(await confirmDialog("Delete this injury record? This cannot be undone.", { confirmLabel: "Delete", danger: true }))) return;
    setSaving(true);
    setError("");
    try {
      await api.delete(`/injuries/${injury.id}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const updates = useMemo(() => injury?.updates || [], [injury]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[180] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="injury-form-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="card w-full max-w-2xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-rose-300/80">
              <Activity size={12} /> {isEdit ? "Edit injury/illness" : "Log injury/illness"}
            </div>
            <h2 className="font-display text-xl text-slate-100 mt-0.5">{sailor?.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="pill active disabled:opacity-50"
              data-testid="injury-save-btn-top"
              title="Save"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={onClose} className="pill !p-1.5" data-testid="injury-close-btn">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="form-input"
              data-testid="injury-type"
            >
              {(constants?.types || []).map((t) => (
                <option key={t} value={t}>{typeLabel(t)}</option>
              ))}
            </select>
            {form.type === "other" && (
              <input
                type="text"
                value={form.type_other}
                onChange={(e) => setForm({ ...form, type_other: e.target.value })}
                placeholder="Describe the injury type"
                className="form-input mt-1.5"
                data-testid="injury-type-other"
                autoFocus
              />
            )}
          </Field>
          <Field label="Body part">
            <select
              value={form.body_part}
              onChange={(e) => setForm({ ...form, body_part: e.target.value })}
              className="form-input"
              data-testid="injury-body-part"
            >
              {(constants?.body_parts || []).map((b) => (
                <option key={b} value={b}>{bodyPartLabel(b)}</option>
              ))}
            </select>
            {form.body_part === "general" && (
              <input
                type="text"
                value={form.body_part_other}
                onChange={(e) => setForm({ ...form, body_part_other: e.target.value })}
                placeholder="Specify the body part (e.g. jaw, scalp, ribcage)"
                className="form-input mt-1.5"
                data-testid="injury-body-part-other"
              />
            )}
          </Field>
          <Field label="Severity">
            <div className="flex gap-1.5" data-testid="injury-severity">
              {(constants?.severities || []).map((s) => {
                const active = form.severity === s;
                const c = SEVERITY_COLOR[s] || "#94A3B8";
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm({ ...form, severity: s })}
                    className="flex-1 py-1.5 rounded-md text-xs border transition"
                    style={{
                      background: active ? `${c}22` : "transparent",
                      borderColor: active ? c : "#1E304B",
                      color: active ? c : "#94A3B8",
                    }}
                    data-testid={`injury-severity-${s}`}
                  >
                    {SEVERITY_LABEL[s] || s}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Onset date">
            <input
              type="date"
              value={form.onset_date}
              onChange={(e) => setForm({ ...form, onset_date: e.target.value })}
              className="form-input"
              data-testid="injury-onset-date"
            />
          </Field>

          <Field label="Description" full>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What happened? (1 line)"
              className="form-input"
              data-testid="injury-description"
            />
          </Field>
          <Field label="Treatment given" full>
            <input
              type="text"
              value={form.treatment}
              onChange={(e) => setForm({ ...form, treatment: e.target.value })}
              placeholder="e.g. Ice + bandage, paracetamol, antiseptic"
              className="form-input"
              data-testid="injury-treatment"
            />
          </Field>

          <Field label="Rest required?">
            <label className="flex items-center gap-2 mt-1.5">
              <input
                type="checkbox"
                checked={form.rest_required}
                onChange={(e) => setForm({ ...form, rest_required: e.target.checked })}
                className="accent-cyan-400 w-4 h-4"
                data-testid="injury-rest-required"
              />
              <span className="text-sm text-slate-300">Athlete on rest from training</span>
            </label>
          </Field>
          <Field label="Days off training">
            <input
              type="number"
              min="0"
              value={form.days_off_training}
              onChange={(e) => setForm({ ...form, days_off_training: e.target.value })}
              placeholder="—"
              className="form-input no-spinner"
              data-testid="injury-days-off"
            />
          </Field>

          {isEdit && (
            <>
              <Field label="Status">
                <div className="flex gap-1.5" data-testid="injury-status">
                  {(constants?.statuses || []).map((s) => {
                    const active = form.status === s;
                    const c = STATUS_COLOR[s] || "#94A3B8";
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm({ ...form, status: s })}
                        className="flex-1 py-1.5 rounded-md text-xs border transition"
                        style={{
                          background: active ? `${c}22` : "transparent",
                          borderColor: active ? c : "#1E304B",
                          color: active ? c : "#94A3B8",
                        }}
                        data-testid={`injury-status-${s}`}
                      >
                        {STATUS_LABEL[s] || s}
                      </button>
                    );
                  })}
                </div>
              </Field>
              {form.status === "resolved" && (
                <Field label="Resolved date">
                  <input
                    type="date"
                    value={form.resolved_date || todayISO()}
                    onChange={(e) => setForm({ ...form, resolved_date: e.target.value })}
                    className="form-input"
                    data-testid="injury-resolved-date"
                  />
                </Field>
              )}
              <Field label="Add timeline note (optional)" full>
                <input
                  type="text"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="e.g. Swelling reduced, light training resumed"
                  className="form-input"
                  data-testid="injury-note-draft"
                />
              </Field>
            </>
          )}
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* YCH Incident Form sections — collapsed by default. Tap each header */}
        {/* to expand and fill in only what's relevant to this incident.       */}
        {/* ------------------------------------------------------------------ */}
        <IncidentSections form={form} setForm={setForm} constants={constants} />

        {isEdit && updates.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">
              Timeline
            </div>
            <div className="space-y-1 max-h-[140px] overflow-y-auto pr-1">
              {updates.map((u) => (
                <div key={u.id} className="text-xs flex gap-2 items-baseline">
                  <span className="font-mono-data text-cyan-300 shrink-0">{u.date}</span>
                  <span className="text-slate-300">{u.note}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Duplicate of the top error banner so it's also visible next to the Save button */}
        {error && (
          <div className="mt-4" data-testid="injury-form-error-bottom">
            <FormError message={error} onDismiss={() => setError("")} testId="injury-form-error" />
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-[#1E304B]">
          <div className="flex items-center gap-2">
            {isEdit && (
              <>
                {form.status !== "resolved" && (
                  <button
                    onClick={resolveNow}
                    disabled={saving}
                    className="pill disabled:opacity-50"
                    style={{ borderColor: "#10B98166", color: "#6EE7B7" }}
                    data-testid="injury-mark-resolved-btn"
                  >
                    <CheckCircle2 size={13} /> Mark resolved
                  </button>
                )}
                <button
                  onClick={remove}
                  disabled={saving}
                  className="pill disabled:opacity-50"
                  style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
                  data-testid="injury-delete-btn"
                >
                  <AlertTriangle size={13} /> Delete
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="pill" data-testid="injury-cancel-btn">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="pill active disabled:opacity-50"
              data-testid="injury-save-btn"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {isEdit ? "Save changes" : "Log injury/illness"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ----------------------------------------------------------------------------
// IncidentSections — four collapsible accordion cards mapping the YCH paper
// incident form into the digital flow. Each section is independently
// expandable; nothing is required. A small badge on the header shows the
// count of fields the coach has already filled so they can see at a glance
// which sections have data.
// ----------------------------------------------------------------------------
function IncidentSections({ form, setForm, constants }) {
  const incidentTypes = constants?.incident_types || [];
  const actionOptions = constants?.actions || [];
  const informedRoles = constants?.informed_roles || [];

  const toggleAction = (val) => {
    const has = (form.actions || []).includes(val);
    setForm({
      ...form,
      actions: has
        ? form.actions.filter((a) => a !== val)
        : [...(form.actions || []), val],
    });
  };

  // Header count helpers — used as the small "3/5 filled" badge per section
  const detailsFilled = [
    form.incident_type, form.time_of_incident, form.reported_by,
    form.report_received_by, form.location,
  ].filter((s) => (s || "").trim()).length;
  const vitalsFilled = [
    form.vitals_bp, form.vitals_temp, form.vitals_pr,
    form.vitals_spo2, form.vitals_sugar, form.symptoms,
  ].filter((s) => (s || "").trim()).length;
  const actionsFilled = (form.actions || []).length;
  const informedFilled = [form.informed_role, form.informed_name, form.informed_time]
    .filter((s) => (s || "").trim()).length;
  const referralFilled = [
    form.ref_hospital, form.ref_time_left, form.ref_time_arrived, form.ref_doctor,
    form.ref_escort, form.ref_contact, form.ref_parent_arrival, form.ref_handover,
  ].filter((s) => (s || "").trim()).length;

  return (
    <div className="mt-5 space-y-2" data-testid="incident-sections">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300/80 mb-1 flex items-center gap-2">
        <span>YCH Incident Form fields</span>
        <span className="text-[10px] text-slate-500 normal-case tracking-normal">— expand any section as needed</span>
      </div>

      <Accordion title="Incident details" count={detailsFilled} testid="acc-details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Incident type">
            <div className="grid grid-cols-2 gap-1.5" data-testid="incident-type">
              {incidentTypes.map((t) => {
                const active = form.incident_type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ ...form, incident_type: t.value })}
                    className="py-1.5 rounded-md text-xs border transition"
                    style={{
                      background: active ? "rgba(0,229,255,0.12)" : "transparent",
                      borderColor: active ? "#00E5FF" : "#1E304B",
                      color: active ? "#7FE7FF" : "#94A3B8",
                    }}
                    data-testid={`incident-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Time of incident">
            <input type="time" value={form.time_of_incident}
              onChange={(e) => setForm({ ...form, time_of_incident: e.target.value })}
              className="form-input" data-testid="incident-time" />
          </Field>
          <Field label="Reported by">
            <input type="text" value={form.reported_by}
              onChange={(e) => setForm({ ...form, reported_by: e.target.value })}
              placeholder="Who first saw / reported the incident"
              className="form-input" data-testid="incident-reported-by" />
          </Field>
          <Field label="Report received by">
            <input type="text" value={form.report_received_by}
              onChange={(e) => setForm({ ...form, report_received_by: e.target.value })}
              placeholder="Who is filling this report"
              className="form-input" data-testid="incident-received-by" />
          </Field>
          <Field label="Location" full>
            <input type="text" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. YCH Dock 3, training pool, parking lot"
              className="form-input" data-testid="incident-location" />
          </Field>
        </div>
      </Accordion>

      <Accordion title="Vitals & symptoms" count={vitalsFilled} testid="acc-vitals">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Field label="BP">
            <input type="text" value={form.vitals_bp}
              onChange={(e) => setForm({ ...form, vitals_bp: e.target.value })}
              placeholder="120/80" className="form-input" data-testid="vital-bp" />
          </Field>
          <Field label="Temp">
            <input type="text" value={form.vitals_temp}
              onChange={(e) => setForm({ ...form, vitals_temp: e.target.value })}
              placeholder="98.4 F" className="form-input" data-testid="vital-temp" />
          </Field>
          <Field label="PR">
            <input type="text" value={form.vitals_pr}
              onChange={(e) => setForm({ ...form, vitals_pr: e.target.value })}
              placeholder="bpm" className="form-input" data-testid="vital-pr" />
          </Field>
          <Field label="SpO₂">
            <input type="text" value={form.vitals_spo2}
              onChange={(e) => setForm({ ...form, vitals_spo2: e.target.value })}
              placeholder="98%" className="form-input" data-testid="vital-spo2" />
          </Field>
          <Field label="Sugar">
            <input type="text" value={form.vitals_sugar}
              onChange={(e) => setForm({ ...form, vitals_sugar: e.target.value })}
              placeholder="mg/dL" className="form-input" data-testid="vital-sugar" />
          </Field>
        </div>
        <label className="block mt-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Symptoms</div>
          <textarea
            value={form.symptoms}
            onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
            placeholder="Free-text observation — e.g. dizzy, vomited once, complaining of headache"
            className="form-input min-h-[64px]"
            rows={2}
            data-testid="incident-symptoms"
          />
        </label>
      </Accordion>

      <Accordion title="Immediate action taken" count={actionsFilled} testid="acc-actions">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {actionOptions.map((a) => {
            const checked = (form.actions || []).includes(a.value);
            return (
              <label
                key={a.value}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
                  checked
                    ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200"
                    : "border-[#1E304B] hover:bg-[#0E1A2E] text-slate-300"
                }`}
                data-testid={`action-${a.value}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleAction(a.value)}
                  className="accent-emerald-400 w-4 h-4"
                />
                <span className="text-sm">{a.label}</span>
              </label>
            );
          })}
        </div>
      </Accordion>

      <Accordion title="Person informed" count={informedFilled} testid="acc-informed">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Role">
            <select value={form.informed_role}
              onChange={(e) => setForm({ ...form, informed_role: e.target.value })}
              className="form-input" data-testid="informed-role">
              <option value="">— Not yet —</option>
              {informedRoles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Name">
            <input type="text" value={form.informed_name}
              onChange={(e) => setForm({ ...form, informed_name: e.target.value })}
              placeholder="Mrs Komaravelly" className="form-input"
              data-testid="informed-name" />
          </Field>
          <Field label="Time informed">
            <input type="time" value={form.informed_time}
              onChange={(e) => setForm({ ...form, informed_time: e.target.value })}
              className="form-input" data-testid="informed-time" />
          </Field>
        </div>
      </Accordion>

      <Accordion title="Hospital / clinic referral" count={referralFilled} testid="acc-referral">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Hospital / clinic" full>
            <input type="text" value={form.ref_hospital}
              onChange={(e) => setForm({ ...form, ref_hospital: e.target.value })}
              placeholder="Apollo, Jubilee Hills" className="form-input" data-testid="ref-hospital" />
          </Field>
          <Field label="Doctor consulted">
            <input type="text" value={form.ref_doctor}
              onChange={(e) => setForm({ ...form, ref_doctor: e.target.value })}
              placeholder="Dr Aditya" className="form-input" data-testid="ref-doctor" />
          </Field>
          <Field label="Escort name">
            <input type="text" value={form.ref_escort}
              onChange={(e) => setForm({ ...form, ref_escort: e.target.value })}
              placeholder="Coach Mia" className="form-input" data-testid="ref-escort" />
          </Field>
          <Field label="Contact no.">
            <input type="text" value={form.ref_contact}
              onChange={(e) => setForm({ ...form, ref_contact: e.target.value })}
              placeholder="+91…" className="form-input" data-testid="ref-contact" />
          </Field>
          <Field label="Time left YCH">
            <input type="time" value={form.ref_time_left}
              onChange={(e) => setForm({ ...form, ref_time_left: e.target.value })}
              className="form-input" data-testid="ref-time-left" />
          </Field>
          <Field label="Time arrived hospital">
            <input type="time" value={form.ref_time_arrived}
              onChange={(e) => setForm({ ...form, ref_time_arrived: e.target.value })}
              className="form-input" data-testid="ref-time-arrived" />
          </Field>
          <Field label="Parent arrival">
            <input type="time" value={form.ref_parent_arrival}
              onChange={(e) => setForm({ ...form, ref_parent_arrival: e.target.value })}
              className="form-input" data-testid="ref-parent-arrival" />
          </Field>
          <Field label="Handover to parent">
            <input type="time" value={form.ref_handover}
              onChange={(e) => setForm({ ...form, ref_handover: e.target.value })}
              className="form-input" data-testid="ref-handover" />
          </Field>
        </div>
      </Accordion>
    </div>
  );
}

function Accordion({ title, count, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#1E304B] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#0E1A2E] transition"
        data-testid={testid}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-200">{title}</span>
          {count > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-mono-data">
              {count} filled
            </span>
          )}
        </div>
        <span className="text-slate-500 text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open && <div className="px-3 pb-3 pt-1 border-t border-[#1E304B]">{children}</div>}
    </div>
  );
}
